import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Close, Create } from "@mui/icons-material";
import { Box, Link, Typography } from "@mui/material";

import { MAX_SECONDARY_RESIDENCE_LIABILITY_ADDRESSES } from "@covertree/consts";
import { SecondaryResidenceLiabilityAddress } from "@covertree/new-business-graphql";
import { theme } from "@covertree/theme";
import { Button, FormTextField, FormToggleCard, Modal } from "@covertree/ui";

import { CustomizePlanCoverage, PolicyCustomizationForm } from "../..";

interface SecondaryResidenceLiabilityContentProps {
  coverage: CustomizePlanCoverage;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  name: any;
  disabled?: boolean;
}

export function SecondaryResidenceLiabilityCustomizationCard({
  coverage: { details, defaultValue, disabled: coverageDisabled },
  name,
  disabled,
}: SecondaryResidenceLiabilityContentProps) {
  const {
    control,
    clearErrors,
    formState: { isValid },
    trigger,
    getValues,
    setValue,
  } = useFormContext<PolicyCustomizationForm>();

  const [valuesOnModalOpen, setValuesOnModalOpen] = useState<
    SecondaryResidenceLiabilityAddress[]
  >([]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { append, remove, fields } = useFieldArray({
    control,
    name,
  });

  const handleAddAddress = useCallback(() => {
    append({ address: "" });
  }, [append]);

  const handleDeleteItem = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleOpenModal = useCallback(
    async (isEdit = false) => {
      if (!isEdit)
        setValue(
          name,
          structuredClone(defaultValue) as SecondaryResidenceLiabilityAddress[],
          {
            shouldDirty: true,
          }
        );
      const currentValues = getValues(name);
      setValuesOnModalOpen(structuredClone(currentValues));

      if (currentValues.length === 0) {
        handleAddAddress();
        clearErrors(name);
      }

      setIsModalOpen(true);
    },
    [isModalOpen, trigger, setIsModalOpen, defaultValue]
  );

  const handleCloseModal = useCallback(() => {
    setValue(name, valuesOnModalOpen);
    setIsModalOpen(false);
  }, [valuesOnModalOpen, setIsModalOpen]);

  const handleSave = useCallback(() => {
    const addresses = getValues(name);
    if (addresses[0].address.length === 0) {
      remove(0);
    }
    setIsModalOpen(false);
  }, [remove, getValues, handleCloseModal]);

  const showAddAddressButton = useMemo(
    () => fields.length < MAX_SECONDARY_RESIDENCE_LIABILITY_ADDRESSES,
    [fields]
  );

  const nameText = useMemo(() => details?.getName(), [details]);
  const tooltipText = useMemo(() => details?.getTooltip(), [details]);

  return (
    <FormToggleCard
      title={nameText}
      image={details.image}
      tooltip={tooltipText}
      subheader={details.getDescription()}
      subheaderVariant="grey"
      disabled={disabled || coverageDisabled}
      onToggle={
        fields.length > 0
          ? () => {
              remove();
            }
          : () => handleOpenModal(false)
      }
      checked={fields.length > 0}
      checkedCustomStyles={{
        backgroundColor: theme.palette.secondary.contrastText,
      }}
      content={
        <>
          {fields.length > 0 && (
            <Link
              disabled={disabled || coverageDisabled}
              component="button"
              type="button"
              onClick={() => handleOpenModal(true)}
              color={disabled ? "text.disabled" : "secondary"}
              variant="body2"
              underline="always">
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  "& svg": {
                    marginRight: theme.spacing(1),
                  },
                })}>
                <Create fontSize="inherit" />
                Edit
              </Box>
            </Link>
          )}

          <Modal
            maxWidth="sm"
            title="Secondary Residences"
            open={isModalOpen}
            onClose={handleCloseModal}>
            <Box sx={(theme) => ({ minWidth: theme.spacing(60) })}>
              <Typography variant="h5" gutterBottom>
                Address of Secondary Residence(s)
              </Typography>
              {fields.map((item, index, array) => (
                <Box
                  sx={{ display: "flex", flexDirection: "row", flexGrow: 1 }}
                  pb={1}
                  key={`address-item-${item.id}`}>
                  <Box sx={{ flexGrow: 1 }}>
                    <FormTextField
                      control={control}
                      name={`${name}.${index}.address`}
                      dataTestId={`secondary-residence-liability-address-${index}`}
                      id={`secondary-residence-liability-address-${index}`}
                      label=""
                      hiddenLabel
                    />
                  </Box>
                  <Box>
                    {array.length > 1 && (
                      <Link
                        sx={(theme) => ({
                          top: theme.spacing(3),
                          left: theme.spacing(1),
                        })}
                        component="button"
                        onClick={() => handleDeleteItem(index)}
                        variant="body2"
                        underline="always"
                        data-testid="remove-address-button">
                        <Close fontSize="inherit" />
                      </Link>
                    )}
                  </Box>
                </Box>
              ))}

              {showAddAddressButton && (
                <Button
                  variant="outlined"
                  size="small"
                  id="add-next-address"
                  data-testid="add-next-address"
                  onClick={handleAddAddress}>
                  Add next address
                </Button>
              )}

              <Box mt={4}>
                <Button
                  id="save-address-form"
                  data-testid="save-address-form"
                  disabled={!isValid}
                  type="button"
                  onClick={handleSave}>
                  Save
                </Button>
              </Box>
            </Box>
          </Modal>
        </>
      }
    />
  );
}
